// Paydaş-Erişim Şablonu yapılandırması (genişletilmiş şema).
//
//   tabs            : Hangi sekmeleri görebilir + alt detayları
//                     - info  : Proje Bilgileri sekmesi + alt bölümler
//                               (genel, adres, paydaslar)
//                     - files : Proje Dosyaları sekmesi + kapasiteler
//                               (preview, download)
//                     - chat  : Sohbet sekmesi (enabled bool)
//                     - notes : Notlar sekmesi (enabled bool)
//   fileCategories  : Her dosya kategorisi için yetki nesnesi
//                     (enabled, preview, addNote, stakeholderFilter)
//   sheets          : Editor pafta erişimi (kod → bool)
//   editorAccess    : 'view' | 'annotate' — 'edit'/'full' yalnız sahip
//
// Geriye dönük uyum: eski boolean tabanlı yapı normalizeAccessConfig
// içinde otomatik yeni şemaya yükseltilir.

export const ACCESS_TABS = [
  { key: 'info',  label: 'Proje Bilgileri' },
  { key: 'files', label: 'Proje Dosyalari' },
  { key: 'chat',  label: 'Sohbet' },
  { key: 'notes', label: 'Notlar' },
];

// Proje Bilgileri sekmesinin alt bölümleri
export const INFO_SECTIONS = [
  { key: 'genel',     label: 'Genel Bilgiler' },
  { key: 'adres',     label: 'Adres Bilgileri' },
  { key: 'paydaslar', label: 'Paydaslar' },
];

// Proje Dosyaları sekmesinin yetki bayrakları
export const FILES_CAPABILITIES = [
  { key: 'preview',  label: 'Ön izleme' },
  { key: 'download', label: 'İndirme' },
];

export const ACCESS_FILE_CATEGORIES = [
  { key: 'mimari_dxf',          label: 'Mimari' },
  { key: 'jbz',                 label: 'JBZ Dosyaları' },
  { key: 'elektrik_dxf',        label: 'Kuvvetli Akım' },
  { key: 'zayif_akim_dxf',      label: 'Zayıf Akım' },
  { key: 'yangin_algilama_dxf', label: 'Yangın Algılama' },
  { key: 'santiye_dxf',         label: 'Şantiye' },
];

// Her dosya kategorisi için ayarlanabilir yetkiler.
// download: kullanıcının dosyayı kendi cihazına indirebilmesi (kapalıyken
// yalnızca tarayıcı içi önizleme/not ekleme yapılabilir).
export const FILE_CATEGORY_CAPABILITIES = [
  { key: 'preview',           label: 'Ön izleme' },
  { key: 'download',          label: 'İndirme' },
  { key: 'addNote',           label: 'Not ekleme' },
  { key: 'stakeholderFilter', label: 'Paydaş filtresi' },
];

export const ACCESS_SHEETS = [
  { key: 'strong_current',    label: 'Kuvvetli Akım' },
  { key: 'weak_current',      label: 'Zayıf Akım' },
  { key: 'fire_detection',    label: 'Yangın Algılama' },
  { key: 'construction_site', label: 'Şantiye' },
  { key: 'connection_line',   label: 'Bağ Hattı' },
];

// Editör yetki seviyeleri — 'edit' ve 'full' yalnız proje sahibinde,
// paydaş şablonlarında seçilemez.
export const EDITOR_ACCESS_LEVELS = [
  { key: 'view',     label: 'Goruntule' },
  { key: 'annotate', label: 'Yorumla / Not Ekle' },
];

// ─── Default şablon ───
// Genel paydaş için makul varsayılan: bilgileri görür, tüm dosyaları (JBZ
// dahil) önizler ve not ekleyebilir, ancak indirme kapalıdır. JBZ'nin sadece
// disiplinin müellifi (şimdilik elektrikMuellif) tarafından indirilmesi için
// rol bazlı önerilen config'lerde download:true olarak ayarlanır.
export const DEFAULT_ACCESS_CONFIG = {
  tabs: {
    info:  { enabled: true,  sections: { genel: true, adres: true, paydaslar: false } },
    files: { enabled: true,  preview: true, download: false },
    chat:  { enabled: true },
    notes: { enabled: true },
  },
  fileCategories: {
    mimari_dxf:          { enabled: true,  preview: true,  download: false, addNote: true,  stakeholderFilter: true },
    jbz:                 { enabled: true,  preview: true,  download: false, addNote: true,  stakeholderFilter: false },
    elektrik_dxf:        { enabled: true,  preview: true,  download: false, addNote: true,  stakeholderFilter: true },
    zayif_akim_dxf:      { enabled: true,  preview: true,  download: false, addNote: true,  stakeholderFilter: true },
    yangin_algilama_dxf: { enabled: true,  preview: true,  download: false, addNote: true,  stakeholderFilter: true },
    santiye_dxf:         { enabled: true,  preview: true,  download: false, addNote: true,  stakeholderFilter: true },
  },
  sheets: {
    strong_current: true,
    weak_current: true,
    fire_detection: true,
    construction_site: true,
    connection_line: true,
  },
  editorAccess: 'view',
};

// Helpers — eski şemadan yenisine yükseltme
function normTab(v, defaults) {
  if (v == null) return { ...defaults };
  if (typeof v === 'boolean') return { ...defaults, enabled: v };
  return { ...defaults, ...v };
}
function normCat(v, defaults) {
  if (v == null) return { ...defaults };
  if (typeof v === 'boolean') return { ...defaults, enabled: v };
  return { ...defaults, ...v };
}

// Boş/eksik bir config'i default ile tamamla — UI editörü ve resolve
// fonksiyonu bunu kullanır. Eski boolean tabanlı tabs/fileCategories
// otomatik yeni şemaya yükseltilir.
export function normalizeAccessConfig(cfg) {
  const base = DEFAULT_ACCESS_CONFIG;
  const c = cfg || {};
  const inTabs = c.tabs || {};
  const inCats = c.fileCategories || {};
  const tabs = {
    info:  normTab(inTabs.info,  base.tabs.info),
    files: normTab(inTabs.files, base.tabs.files),
    chat:  normTab(inTabs.chat,  base.tabs.chat),
    notes: normTab(inTabs.notes, base.tabs.notes),
  };
  // Alt bölümler/yetkiler her zaman tam set olsun
  tabs.info.sections = { ...base.tabs.info.sections, ...(tabs.info.sections || {}) };
  // info.enabled false ise alt bölümler eski boolean değerine düşmesin —
  // mevcut sections değerleri korunsun
  const fileCategories = {};
  for (const cat of ACCESS_FILE_CATEGORIES) {
    fileCategories[cat.key] = normCat(inCats[cat.key], base.fileCategories[cat.key]);
  }
  // Editor erişim — 'edit'/'full' geldiyse 'annotate'e indir
  let editorAccess = c.editorAccess || base.editorAccess;
  if (editorAccess !== 'view' && editorAccess !== 'annotate') editorAccess = 'annotate';
  return {
    tabs,
    fileCategories,
    sheets: { ...base.sheets, ...(c.sheets || {}) },
    editorAccess,
  };
}

// Paydaş türüne göre tipik öneri (rol tabanlı varsayılanlar). Şablon
// seçilmemiş paydaşlar için resolveAccessConfig fallback'idir.
export const ROLE_SUGGESTED_CONFIG = {
  malSahibi: normalizeAccessConfig({
    tabs: {
      info: { enabled: true, sections: { genel: true, adres: true, paydaslar: true } },
      files: { enabled: true, preview: true, download: false },
    },
    fileCategories: {
      santiye_dxf: { enabled: false, preview: false, addNote: false, stakeholderFilter: false },
    },
    editorAccess: 'view',
  }),
  client: normalizeAccessConfig({
    tabs: {
      info: { enabled: true, sections: { genel: true, adres: true, paydaslar: false } },
      files: { enabled: true, preview: true, download: false },
    },
    editorAccess: 'view',
  }),
  muteahhit: normalizeAccessConfig({
    tabs: {
      info: { enabled: true, sections: { genel: true, adres: true, paydaslar: true } },
      files: { enabled: true, preview: true, download: true },
    },
    editorAccess: 'view',
  }),
  mimariMuellif: normalizeAccessConfig({
    tabs: {
      info: { enabled: true, sections: { genel: true, adres: true, paydaslar: true } },
      files: { enabled: true, preview: true, download: true },
    },
    fileCategories: {
      mimari_dxf:          { enabled: true,  preview: true,  addNote: true,  stakeholderFilter: true },
      elektrik_dxf:        { enabled: false },
      zayif_akim_dxf:      { enabled: false },
      yangin_algilama_dxf: { enabled: false },
      santiye_dxf:         { enabled: false },
    },
    sheets: {
      strong_current: false, weak_current: false,
      fire_detection: false, construction_site: false, connection_line: false,
    },
    editorAccess: 'annotate',
  }),
  elektrikMuellif: normalizeAccessConfig({
    tabs: {
      info: { enabled: true, sections: { genel: true, adres: true, paydaslar: true } },
      files: { enabled: true, preview: true, download: true },
    },
    fileCategories: {
      // Disiplinin (Elektrik) müellifi olduğu için JBZ indirme yetkisi açık.
      jbz: { enabled: true, preview: true, download: true, addNote: true, stakeholderFilter: false },
    },
    editorAccess: 'annotate',
  }),
  mekanikMuellif: normalizeAccessConfig({
    tabs: {
      info: { enabled: true, sections: { genel: true, adres: true, paydaslar: true } },
      files: { enabled: true, preview: true, download: true },
    },
    fileCategories: {
      mimari_dxf:          { enabled: true,  preview: true,  addNote: false, stakeholderFilter: false },
      elektrik_dxf:        { enabled: false },
      zayif_akim_dxf:      { enabled: false },
      yangin_algilama_dxf: { enabled: false },
      santiye_dxf:         { enabled: false },
    },
    editorAccess: 'annotate',
  }),
  kontrolMuhendis: normalizeAccessConfig({
    tabs: {
      info: { enabled: true, sections: { genel: true, adres: true, paydaslar: true } },
      files: { enabled: true, preview: true, download: true },
    },
    editorAccess: 'annotate',
  }),
  yapiDenetimId: normalizeAccessConfig({
    tabs: {
      info: { enabled: true, sections: { genel: true, adres: true, paydaslar: false } },
      files: { enabled: true, preview: true, download: true },
    },
    editorAccess: 'view',
  }),
  elektrikDagitimId: normalizeAccessConfig({
    tabs: {
      info: { enabled: true, sections: { genel: true, adres: true, paydaslar: false } },
      files: { enabled: true, preview: true, download: false },
      chat: { enabled: false },
      notes: { enabled: false },
    },
    fileCategories: {
      mimari_dxf:          { enabled: false },
      elektrik_dxf:        { enabled: true, preview: true, addNote: false, stakeholderFilter: false },
      zayif_akim_dxf:      { enabled: false },
      yangin_algilama_dxf: { enabled: false },
      santiye_dxf:         { enabled: false },
    },
    editorAccess: 'view',
  }),
  telekomId: normalizeAccessConfig({
    tabs: {
      info: { enabled: true, sections: { genel: true, adres: true, paydaslar: false } },
      files: { enabled: true, preview: true, download: false },
      chat: { enabled: false },
      notes: { enabled: false },
    },
    fileCategories: {
      mimari_dxf:          { enabled: false },
      elektrik_dxf:        { enabled: false },
      zayif_akim_dxf:      { enabled: true, preview: true, addNote: false, stakeholderFilter: false },
      yangin_algilama_dxf: { enabled: false },
      santiye_dxf:         { enabled: false },
    },
    editorAccess: 'view',
  }),
  itfaiyeId: normalizeAccessConfig({
    tabs: {
      info: { enabled: true, sections: { genel: true, adres: true, paydaslar: false } },
      files: { enabled: true, preview: true, download: false },
      chat: { enabled: false },
      notes: { enabled: false },
    },
    fileCategories: {
      mimari_dxf:          { enabled: false },
      elektrik_dxf:        { enabled: false },
      zayif_akim_dxf:      { enabled: false },
      yangin_algilama_dxf: { enabled: true, preview: true, addNote: false, stakeholderFilter: false },
      santiye_dxf:         { enabled: false },
    },
    editorAccess: 'view',
  }),
  belediyeId: normalizeAccessConfig({
    tabs: {
      info: { enabled: true, sections: { genel: true, adres: true, paydaslar: false } },
      files: { enabled: true, preview: true, download: false },
      chat: { enabled: false },
      notes: { enabled: false },
    },
    editorAccess: 'view',
  }),
};

// Rol-bazlı veya default config'i çöz
export function resolveAccessConfig(stakeholderKey, override, templateConfig) {
  if (override) return normalizeAccessConfig(override);
  if (templateConfig) return normalizeAccessConfig(templateConfig);
  return ROLE_SUGGESTED_CONFIG[stakeholderKey] || normalizeAccessConfig(DEFAULT_ACCESS_CONFIG);
}

// Bir paydaşın metadata kaydı — yeni eklemede iskelet
export function emptyStakeholderAccess() {
  return { templateId: '', override: null, shareToken: '' };
}

// Yardımcı: bir kategoriyi indirme yetkisi var mı? Normalize edilmiş config
// üzerinde çalışır. tabs.files.download false ise tüm kategoriler kapalıdır.
export function canDownloadCategory(accessConfig, categoryKey) {
  if (!accessConfig) return false;
  const cfg = normalizeAccessConfig(accessConfig);
  if (!cfg.tabs?.files?.enabled) return false;
  // Kategori bazında download flag'i
  const cat = cfg.fileCategories?.[categoryKey];
  if (cat && Object.prototype.hasOwnProperty.call(cat, 'download')) {
    return !!cat.download;
  }
  // Eski şema: kategori bazında download yoksa global tabs.files.download
  return !!cfg.tabs.files.download;
}
